import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

const AuthContext = createContext();

const SUPER_ADMIN_EMAIL = 'aperez@motivy.co';
const ALLOWED_DOMAIN = 'motivy.co';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const checkAdmin = (u) => {
    if (!u?.email) return false;
    return u.email === SUPER_ADMIN_EMAIL || u.email.endsWith(`@${ALLOWED_DOMAIN}`);
  };

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    // Handle the initial session + hash tokens from OAuth redirect
    const initAuth = async () => {
      try {
        // This will automatically pick up tokens from the URL hash if present
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Auth session error:', error);
        }
        
        const u = session?.user || null;
        setUser(u);
        setIsAdmin(checkAdmin(u));

        // Clean up the URL hash after processing tokens
        if (window.location.hash && window.location.hash.includes('access_token')) {
          window.history.replaceState(null, '', window.location.pathname);
        }
      } catch (err) {
        console.error('Auth init error:', err);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user || null;
      setUser(u);
      setIsAdmin(checkAdmin(u));
      setLoading(false);

      // Clean up URL hash after OAuth callback
      if (window.location.hash && window.location.hash.includes('access_token')) {
        window.history.replaceState(null, '', window.location.pathname);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    if (!supabase) return;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/admin/blog`,
      },
    });
    if (error) console.error('Auth error:', error);
  };

  const signOut = async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    setUser(null);
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, isAdmin, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
